import { useState, useEffect, useCallback, useRef } from 'react';
import { settingsAPI } from '../services/api';
import {
  getCached,
  setCached,
  invalidate as invalidateCache,
  hasCached,
} from '../services/settingsListCache';

/**
 * useSettingsList — shared hook that fetches a settings list (job_types,
 * lead_sources, customer_types, crews, resources, service_areas, tax_rates)
 * and returns live options for dropdowns across the app.
 *
 * Results are cached in `services/settingsListCache` so multiple components
 * mounting the same list type don't each hit the network. The cache is
 * INVALIDATED AT THE WRITER (settingsAPI.addListItem / updateListItem /
 * deleteListItem / reorderList in services/api.js) — see that module's
 * cache-invalidation section for the rationale. Call `refetch()` for
 * manual invalidate + reload of a single hook instance.
 *
 * @param {string} listType e.g. 'job_types', 'lead_sources', 'crews'
 * @returns {{ items: Array, loading: boolean, error: any, refetch: () => void }}
 */

// Backward-compat re-export: many test files import clearSettingsListCache
// from here. The cache itself now lives in services/settingsListCache; this
// wrapper preserves the historical import path.
export function clearSettingsListCache(listType) {
  invalidateCache(listType);
}

export function useSettingsList(listType) {
  const [items, setItems] = useState(() => getCached(listType)?.items || []);
  const [loading, setLoading] = useState(() => !hasCached(listType));
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async (force = false) => {
    if (!listType) {
      setLoading(false);
      return;
    }
    if (!force && hasCached(listType)) {
      setItems(getCached(listType).items);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await settingsAPI.getList(listType);
      // Backend may return the array directly or wrapped in { data: [...] }.
      const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      // Only include active items for dropdowns.
      const active = arr.filter((it) => it && it.active !== false);
      setCached(listType, { items: active });
      if (mounted.current) setItems(active);
    } catch (err) {
      if (mounted.current) setError(err);
      // eslint-disable-next-line no-console
      console.error(`useSettingsList(${listType}) failed:`, err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [listType]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const refetch = useCallback(() => {
    clearSettingsListCache(listType);
    return load(true);
  }, [listType, load]);

  return { items, loading, error, refetch };
}

export default useSettingsList;
